import React, { useState } from "react";
import clsx from "clsx";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { Mail, Settings2, Trash2 } from "lucide-react";

const DotsIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <circle cx="12" cy="5" r="2" fill="currentColor" />
    <circle cx="12" cy="12" r="2" fill="currentColor" />
    <circle cx="12" cy="19" r="2" fill="currentColor" />
  </svg>
);

/**
 * A switch drawn as a button, so it works with the keyboard and announces its state. The knob's
 * position carries the state as well as the colour.
 */
const Switch = ({ checked, disabled, onToggle, label }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    aria-label={label}
    disabled={disabled}
    onClick={(e) => {
      // Stays inside the menu item, so Radix does not treat the toggle as a selection.
      e.stopPropagation();
      if (!disabled) onToggle(!checked);
    }}
    className={clsx(
      "relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-100",
      checked ? "bg-blue-100" : "bg-dimgray-400",
      disabled && "cursor-not-allowed opacity-50"
    )}
  >
    <span
      className={clsx(
        "inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform",
        checked ? "translate-x-[18px]" : "translate-x-0.5"
      )}
    />
  </button>
);

const itemClass =
  "flex w-full cursor-pointer select-none items-center gap-2.5 px-3 py-2.5 text-left outline-none data-[highlighted]:bg-whitesmoke-600";

/**
 * The three-dots menu of a scheduled task: the "Send to email" switch, the screen where the
 * recipients are managed, and the delete that used to be a bare trash icon.
 *
 * Built on the Radix dropdown the app already ships, and rendered through its portal: the task
 * list scrolls, and a menu drawn inside a scrolling panel is clipped at the panel's edge.
 *
 * `emailDelivery` is the row's settings as the list API returns them; null means the
 * environment has no email delivery yet, and the two email entries are left out.
 */
const ScheduledItemMenu = ({
  emailDelivery,
  canSendToOwner,
  onToggleEmail,
  onManageEmail,
  onDelete,
  disabled = false,
}) => {
  const [open, setOpen] = useState(false);

  const sendToMe = !!emailDelivery?.sendToMe;
  const extras = emailDelivery?.extraRecipients?.length ?? 0;

  return (
    <DropdownMenu.Root open={open} onOpenChange={setOpen} modal={false}>
      <DropdownMenu.Trigger asChild disabled={disabled}>
        <button
          type="button"
          aria-label="More actions"
          onClick={(e) => e.stopPropagation()}
          // Always drawn, faintly, and stronger under the row's hover: a control that only
          // appears on hover cannot be reached on a touch screen, and a press lands on the row.
          className={clsx(
            "flex h-6 w-6 shrink-0 items-center justify-center rounded-md transition-colors hover:!bg-white hover:!text-dark-blue-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-100",
            open ? "bg-white text-dark-blue-100" : "text-dimgray-400 group-hover:text-dimgray-200",
            disabled && "cursor-not-allowed"
          )}
        >
          <DotsIcon />
        </button>
      </DropdownMenu.Trigger>

      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="end"
          sideOffset={4}
          collisionPadding={8}
          onClick={(e) => e.stopPropagation()}
          className="z-[60] w-[300px] rounded-lg bg-white py-1 font-notosans text-[13px] text-dark-blue-100 shadow-select"
        >
          {emailDelivery && (
            <>
              <DropdownMenu.Item
                onSelect={(e) => e.preventDefault()}
                className={clsx(itemClass, "cursor-default")}
                title={canSendToOwner ? undefined : "Your GreenRoad profile has no email address"}
              >
                <Mail size={16} className="shrink-0 text-blue-100" aria-hidden="true" />
                <span className="flex min-w-0 flex-1 flex-col">
                  <span className="font-semibold">Send to email</span>
                  <span className="truncate text-[12px] text-dimgray-200">
                    {sendToMe && emailDelivery.ownerEmail
                      ? emailDelivery.ownerEmail
                      : canSendToOwner
                        ? "Off for my address"
                        : "No email on your profile"}
                    {extras > 0 && ` · +${extras} more`}
                  </span>
                </span>
                <Switch
                  checked={sendToMe}
                  disabled={disabled || !canSendToOwner}
                  label="Send to my email"
                  onToggle={(next) => onToggleEmail(next)}
                />
              </DropdownMenu.Item>
              <DropdownMenu.Item onSelect={() => onManageEmail()} className={itemClass}>
                <Settings2 size={16} className="shrink-0 text-dimgray-200" aria-hidden="true" />
                <span>Manage email recipients…</span>
              </DropdownMenu.Item>
              <DropdownMenu.Separator className="my-1 border-t border-whitesmoke-100" />
            </>
          )}
          <DropdownMenu.Item onSelect={() => onDelete()} className={clsx(itemClass, "text-red-100")}>
            <Trash2 size={16} className="shrink-0" aria-hidden="true" />
            <span>Delete scheduled task</span>
          </DropdownMenu.Item>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
};

export default ScheduledItemMenu;
